import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// 페이지 레벨 도움말 패널 — 택소노미 5탭 모두에서 공통 접근.
// 내용: Type-A / merge_strategy / relation / Jaccard / 승인 흐름 / reason_code / coverage.
//
// BalanceChartsTab 의 OverlapHelpPanel 과 같은 우측 슬라이드 패턴 (.tx-help-backdrop /
// .tx-help-panel) 재사용. 본 패널은 중복 지표 외 전반 개념을 담고,
// BalanceChartsTab 의 지표별 패널은 별도 유지 (둘은 목적이 다르다).
//
// 접근성:
//   · role="dialog" aria-modal="true"
//   · ESC 로 닫힘
//   · 배경 클릭으로 닫힘 (stopPropagation 으로 패널 내부 클릭 제외)
//   · focus trap 은 단순화 — 첫 포커스는 닫기 버튼.

interface Props {
  onClose: () => void;
}

export function TaxonomyHelpPanel({ onClose }: Props): JSX.Element {
  const closeRef = useRef<HTMLButtonElement>(null);

  // 마운트 시 닫기 버튼 포커스 + ESC 핸들러.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="tx-help-backdrop"
      role="presentation"
      onClick={onClose}
      data-testid="tx-help-panel"
    >
      <aside
        className="tx-help-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tx-page-help-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="tx-help-panel__head">
          <h2 id="tx-page-help-title" className="tx-help-panel__title">
            섹터 택소노미 가이드
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="tx-help-panel__close"
            onClick={onClose}
            aria-label="도움말 닫기"
            data-testid="tx-help-panel-close"
          >
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </header>

        <div className="tx-help-panel__body">
          <section>
            <h3>1. Type-A 리밸런싱은 “전체 교체” 입니다</h3>
            <ul>
              <li>
                <strong>proposed_taxonomy</strong> 는 해당 run 이 승인되면
                새 정규 체계 <em>그 자체</em> 가 됩니다.
              </li>
              <li>
                기존 canonical 은 <code>is_active=False</code> 로 deactivate —
                과거 매핑 참조는 유지되지만 신규 집계에서 제외.
              </li>
              <li>
                기존 매핑은 <code>status='rejected'</code> 로 supersede.
              </li>
              <li>
                신규 매핑은 <code>status='pending_review'</code> — 운영자 승인
                필수.
              </li>
            </ul>
          </section>

          <section>
            <h3>2. 매핑 전략 (merge_strategy)</h3>
            <table className="tx-help-table">
              <thead>
                <tr>
                  <th>값</th>
                  <th>의미</th>
                  <th>참고</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>rename</code>
                  </td>
                  <td>원천 1개 → canonical 1개 (이름만 개선)</td>
                  <td>구조 변경 없음</td>
                </tr>
                <tr>
                  <td>
                    <code>merge</code>
                  </td>
                  <td>원천 N개 → canonical 1개 (통합)</td>
                  <td>
                    <code>merge_basis</code> 필수
                  </td>
                </tr>
                <tr>
                  <td>
                    <code>split</code>
                  </td>
                  <td>1 → N 분할</td>
                  <td>현재 금지</td>
                </tr>
                <tr>
                  <td>
                    <code>new</code>
                  </td>
                  <td>원천에 없던 상위 메타</td>
                  <td>지주사 · 펀드 · ETF 등</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h3>3. 매핑 관계 (relation)</h3>
            <table className="tx-help-table">
              <thead>
                <tr>
                  <th>값</th>
                  <th>의미</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>primary_name</code>
                  </td>
                  <td>원천명이 canonical 의 대표 이름</td>
                </tr>
                <tr>
                  <td>
                    <code>equivalent_synonym</code>
                  </td>
                  <td>이름만 다른 동의어</td>
                </tr>
                <tr>
                  <td>
                    <code>sibling_by_overlap</code>
                  </td>
                  <td>
                    구성 종목 Jaccard 로 묶인 형제 (<code>overlap_ratio</code>{' '}
                    기록)
                  </td>
                </tr>
                <tr>
                  <td>
                    <code>parent_in_source_hierarchy</code>
                  </td>
                  <td>원천 체계상 상위</td>
                </tr>
                <tr>
                  <td>
                    <code>child_in_source_hierarchy</code>
                  </td>
                  <td>원천 체계상 하위</td>
                </tr>
                <tr>
                  <td>
                    <code>cross_source_synonym</code>
                  </td>
                  <td>
                    다른 source (naver_primary ↔ naver_theme) 간 동일 개념
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h3>4. Jaccard 유사도 해석</h3>
            <p>
              공식: <code>교집합 종목 수 / 합집합 종목 수</code> (0~1)
            </p>
            <table className="tx-help-table">
              <thead>
                <tr>
                  <th>구간</th>
                  <th>해석</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>≥ 0.70</code>
                  </td>
                  <td>강한 중복 — merge 강력 권장</td>
                </tr>
                <tr>
                  <td>
                    <code>0.50 ~ 0.70</code>
                  </td>
                  <td>중복 의심 — 검토 후 merge 기본</td>
                </tr>
                <tr>
                  <td>
                    <code>0.30 ~ 0.50</code>
                  </td>
                  <td>약한 유사 — 단독 유지 일반적</td>
                </tr>
                <tr>
                  <td>
                    <code>&lt; 0.30</code>
                  </td>
                  <td>무관</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h3>5. 매핑 승인 흐름</h3>
            <ul>
              <li>
                <code>stock_canonical_sectors</code> VIEW 는{' '}
                <strong>confirmed</strong> 매핑만 따라갑니다.
              </li>
              <li>
                pending 만 있으면 해당 canonical 의{' '}
                <code>symbol_count=0</code> → 트리에 <strong>⚠ 대기</strong>{' '}
                배지.
              </li>
              <li>운영자가 매핑 탭에서 승인하면 VIEW 반영 → 집계 정상화.</li>
              <li>
                일괄 승인: <code>llm_confidence ≥ 0.90</code> 자동 승인 버튼
                제공 (되돌릴 수 없음, 감사 이력에만 기록).
              </li>
            </ul>
          </section>

          <section>
            <h3>6. Uncovered 사유 (reason_code)</h3>
            <table className="tx-help-table">
              <thead>
                <tr>
                  <th>값</th>
                  <th>의미</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>too_broad_meta</code>
                  </td>
                  <td>너무 넓은 메타 (성장주 등)</td>
                </tr>
                <tr>
                  <td>
                    <code>too_narrow_niche</code>
                  </td>
                  <td>너무 좁은 니치</td>
                </tr>
                <tr>
                  <td>
                    <code>conflicts_multiple</code>
                  </td>
                  <td>여러 섹터에 걸쳐 애매</td>
                </tr>
                <tr>
                  <td>
                    <code>low_constituent_count</code>
                  </td>
                  <td>구성 종목 부족</td>
                </tr>
                <tr>
                  <td>
                    <code>no_clear_home</code>
                  </td>
                  <td>명확한 귀속지 없음</td>
                </tr>
                <tr>
                  <td>
                    <code>deferred_new_theme</code>
                  </td>
                  <td>신규 테마 관찰만</td>
                </tr>
                <tr>
                  <td>
                    <code>legacy_unknown</code>
                  </td>
                  <td>시스템 내부 변환용 (LLM 출력 아님)</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h3>7. 커버리지 경고 (coverage_missing)</h3>
            <ul>
              <li>
                수식:{' '}
                <code>
                  input_source_count − (proposed_mapping + uncovered) &gt; 0
                </code>
              </li>
              <li>
                위 조건이면 LLM 이 일부 원천을 누락한 것 →{' '}
                <code>LLM.GATE_E.COVERAGE_MISSING</code> WARN 이벤트 발행.
              </li>
              <li>시스템 로그에서 해당 run_id 검색 후 운영자 수동 보정.</li>
            </ul>
          </section>
        </div>
      </aside>
    </div>
  );
}
